import { useNavigate } from 'react-router-dom';
import { Box, Card, CardActionArea, CardContent, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useState, type MouseEvent } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import type { Newsletter } from '@/types';
import { NewsletterStatusChip } from '@/components/common/Common';
import { formatDate } from '@/utils/dateUtils';
import { markdownToPlainText } from '@/utils/markdown';
import { ROUTES } from '@/constants/routes';

export default function NewsletterCard({
  newsletter,
  onDelete,
}: {
  newsletter: Newsletter;
  onDelete: (newsletter: Newsletter) => void;
}) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const openMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const closeMenu = () => setAnchorEl(null);

  return (
    <Card variant="outlined" sx={{ position: 'relative' }}>
      <CardActionArea onClick={() => navigate(ROUTES.newsletterPreview(newsletter.id))}>
        <CardContent sx={{ pr: 6 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack spacing={0.5}>
              <Typography variant="h4">{newsletter.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(newsletter.periodStart)} – {formatDate(newsletter.periodEnd)}
              </Typography>
              {newsletter.sections.executiveSummary && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {markdownToPlainText(newsletter.sections.executiveSummary)}
                </Typography>
              )}
            </Stack>
            <NewsletterStatusChip status={newsletter.status} />
          </Stack>
        </CardContent>
      </CardActionArea>
      <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
        <IconButton size="small" onClick={openMenu} aria-label="More actions">
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            closeMenu();
            navigate(ROUTES.newsletterEdit(newsletter.id));
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            navigate(ROUTES.newsletterPreview(newsletter.id));
          }}
        >
          Preview
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            navigate(ROUTES.newsletterVersions(newsletter.id));
          }}
        >
          Version History
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            onDelete(newsletter);
          }}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>
    </Card>
  );
}
